// Account context — lifted out of My.Tms.Old/contexts/TenantContext.tsx and
// renamed to use the same vocabulary as the BE / Auth0 claims (the BE has
// always called this an "account"; the FE was the only place using "tenant").
//
// Scope: holds the *currently selected* account id (a GUID) and persists it to
// localStorage. Initialised from `https://my/account_id` if no value is yet
// stored. The list of available accounts is NOT fetched here — that's the
// caller's job (see `useAvailableAccounts` from `@my/directory-api/hooks`),
// which keeps this package free of any directory-api dependency.
//
// Switching accounts is a two-step flow that the consumer composes:
//   1. call `setCurrentAccountId(accountId)` (this provider, persists locally)
//   2. call `getAccessTokenSilently({ authorizationParams: { account_id: ... },
//      cacheMode: 'off' })` to mint a token bound to the new account.
//
// Each suite app has its OWN localStorage (different origin per app), so the
// storage key is namespaced by app code (`my:tms:account_id`,
// `my:depot:account_id`, etc.). Honest naming for what's actually
// per-origin. The JWT claim brings the per-app caches into sync at login,
// so cross-app drift self-heals on next sign-in.

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';

// ────────────────────────────────────────────────────────────────────────────
// Per-app storage configuration
//
// `configureAccountStorage('tms')` must be called at boot (typically in
// `main.tsx`, before any provider mounts) so the storage key is properly
// namespaced. If never configured, falls back to an unprefixed key — works,
// but loses the app namespace; in dev you'll see a console warning the first
// time `getStoredAccountId()` is called without configuration.
// ────────────────────────────────────────────────────────────────────────────

let appCode = '';

/**
 * Set the suite-app identifier used to namespace this app's localStorage
 * keys. Call once at boot, before mounting `<AccountProvider>` or
 * `<Auth0ProviderWithRouter>` (both read storage during render).
 */
export function configureAccountStorage(code: string): void {
  appCode = code;
}

function storageKey(): string {
  if (!appCode) {
    if (typeof console !== 'undefined') {
      console.warn(
        '[@my/platform/account] configureAccountStorage(appCode) was not called before storage access — falling back to "my:account_id". Call it from main.tsx before initialising the auth provider.',
      );
    }
    return 'my:account_id';
  }
  return `my:${appCode}:account_id`;
}

/** Read the account id from localStorage. Returns `''` when unset. */
export function getStoredAccountId(): string {
  if (typeof window === 'undefined') return '';
  return window.localStorage.getItem(storageKey()) ?? '';
}

/** Persist an account id (or remove the entry when given `''`). */
export function setStoredAccountId(accountId: string): void {
  if (typeof window === 'undefined') return;
  const key = storageKey();
  if (accountId) window.localStorage.setItem(key, accountId);
  else window.localStorage.removeItem(key);
}

interface AccountContextValue {
  /** Currently selected account id, or `''` when none is selected yet. */
  currentAccountId: string;
  /** Set the account id (and persist to localStorage). */
  setCurrentAccountId: (accountId: string) => void;
  /** Clear the persisted account — typically called on sign-out. */
  clearAccount: () => void;
}

const AccountContext = createContext<AccountContextValue | undefined>(undefined);

export interface AccountProviderProps {
  /**
   * Fallback account id when localStorage is empty. Apps typically pass the
   * Auth0 `https://my/account_id` claim so the user lands on their default
   * account on first login.
   */
  defaultAccountId?: string;
  children: ReactNode;
}

/**
 * Wrap the authenticated portion of the app. Initialises `currentAccountId`
 * from localStorage and then *always* reconciles with `defaultAccountId`
 * (the JWT's `account_id` claim) once it arrives.
 *
 * Reconciliation rules:
 *  - On boot, the cache is the only source of truth available pre-auth, so
 *    state initialises from `getStoredAccountId()`.
 *  - Once `defaultAccountId` arrives (post-auth) and differs from the cached
 *    value, the JWT wins. This handles the silent-fallback case: cache says
 *    A, user lost access to A, Auth0 Action silently mints a token for B,
 *    we adopt B and update the cache so next boot is clean.
 *  - The user-initiated switch path (`setCurrentAccountId`) updates state +
 *    cache synchronously, then the caller refreshes the JWT silently. By the
 *    time `defaultAccountId` reflects the new account, state already does
 *    too, so the reconciliation effect is a no-op.
 *
 * The cache is the *hint passed to Auth0 next boot*. The JWT claim is the
 * *truth right now*. They can disagree only briefly during a switch and
 * always self-correct.
 */
export function AccountProvider({ defaultAccountId = '', children }: AccountProviderProps) {
  // Initialise from cache only — *not* from `defaultAccountId`. Falling back
  // to the JWT claim here would set state without writing through to
  // localStorage, and under StrictMode (where the initialiser runs on every
  // remount) it would happen with a stale cache, leaving the cache empty
  // forever. Make the reconciliation effect below the *only* path that
  // writes both state and cache.
  const [currentAccountId, setCurrentAccountIdState] = useState<string>(() => getStoredAccountId());

  // Always reconcile with the JWT claim — cache is just the boot hint.
  useEffect(() => {
    if (!defaultAccountId) return;
    if (defaultAccountId === currentAccountId) return;
    setStoredAccountId(defaultAccountId);
    setCurrentAccountIdState(defaultAccountId);
  }, [defaultAccountId, currentAccountId]);

  const setCurrentAccountId = useCallback((accountId: string) => {
    setStoredAccountId(accountId);
    setCurrentAccountIdState(accountId);
    // Caller is responsible for the silent re-auth (`getAccessTokenSilently`
    // with `account_id: <new>`) so the JWT updates to match. Once it does,
    // the reconciliation effect above sees defaultAccountId === current and
    // is a no-op.
  }, []);

  const clearAccount = useCallback(() => {
    setStoredAccountId('');
    setCurrentAccountIdState('');
  }, []);

  return (
    <AccountContext.Provider value={{ currentAccountId, setCurrentAccountId, clearAccount }}>
      {children}
    </AccountContext.Provider>
  );
}

/** Read the account context. Throws when used outside an `<AccountProvider>`. */
export function useAccount(): AccountContextValue {
  const ctx = useContext(AccountContext);
  if (ctx === undefined) {
    throw new Error('useAccount must be used inside <AccountProvider>');
  }
  return ctx;
}
